export function removeEmpty(obj: any) {
   Object.keys(obj).forEach((k) => !obj[k] && obj[k] !== undefined && delete obj[k]);
   return obj;
}

export function getPropertyByKey(key: string, obj: any) {
   if (!obj) return null;

   return key.split('.').reduce((acc, cur) => acc?.[cur], obj);
}

export function findDuplicateObjectIndexes<T extends { [key: string]: any }>(arr: T[], properties: Array<keyof T>): number[] {
   const indexes: number[] = [];
   const unique: Set<string> = new Set();

   for (let i = 0; i < arr.length; i++) {
      const obj = arr[i];
      const key = properties.map((prop) => obj[prop]).join('_');
      if (unique.has(key)) {
         indexes[uniqueIndexForKey(key)] = i;
      } else {
         unique.add(key);
         indexes.push(i);
      }
   }

   function uniqueIndexForKey(key: string): number {
      for (let i = indexes.length - 1; i >= 0; i--) {
         const obj = arr[indexes[i]];
         const objKey = properties.map((prop) => obj[prop]).join('_');
         if (objKey === key) {
            return i;
         }
      }
      // should never reach here unless there is an error in the logic
      throw new Error(`Unable to find unique index for key ${key}`);
   }

   return indexes.filter((index) => index !== undefined);
}

interface ObjectDifference {
   path: string;
   oldValue: any;
   newValue: any;
   type: 'added' | 'removed' | 'modified';
}

/**
 * Compares two objects and logs their differences to the console using console.table
 * @param obj1 - The first object (often the original/old value)
 * @param obj2 - The second object (often the new/updated value)
 * @param label - Optional label to identify the comparison in logs
 * @param maxDepth - Maximum depth to traverse (default: 10)
 */
export function logObjectDifferences(obj1: any, obj2: any, label: string = 'Object Comparison', maxDepth: number = 10): void {
   const differences = getObjectDifferences(obj1, obj2, '', maxDepth);

   if (differences.length === 0) {
      console.log(`🟢 ${label}: No differences found`);
      return;
   }

   console.group(`🔍 ${label}: Found ${differences.length} difference(s)`);

   // Prepare data for console.table
   const tableData = differences.map((diff, index) => {
      const emoji = diff.type === 'added' ? '➕' : diff.type === 'removed' ? '➖' : '🔄';
      const row: any = {
         '#': index + 1,
         Type: `${emoji} ${diff.type.toUpperCase()}`,
         Path: diff.path,
      };

      if (diff.type === 'modified') {
         row['Old Value'] = formatValueForTable(diff.oldValue);
         row['New Value'] = formatValueForTable(diff.newValue);
      } else if (diff.type === 'added') {
         row['Old Value'] = '(not present)';
         row['New Value'] = formatValueForTable(diff.newValue);
      } else if (diff.type === 'removed') {
         row['Old Value'] = formatValueForTable(diff.oldValue);
         row['New Value'] = '(removed)';
      }

      return row;
   });

   // Display the table
   console.table(tableData);

   // Show detailed values for complex objects if needed
   const complexDifferences = differences.filter(
      (diff) => (diff.oldValue && typeof diff.oldValue === 'object') || (diff.newValue && typeof diff.newValue === 'object'),
   );

   if (complexDifferences.length > 0) {
      console.group('📋 Detailed values for complex objects:');
      complexDifferences.forEach((diff, index) => {
         console.group(`${index + 1}. ${diff.path} (${diff.type})`);
         if (diff.type === 'modified') {
            console.log('Old:', diff.oldValue);
            console.log('New:', diff.newValue);
         } else if (diff.type === 'added') {
            console.log('Added:', diff.newValue);
         } else if (diff.type === 'removed') {
            console.log('Removed:', diff.oldValue);
         }
         console.groupEnd();
      });
      console.groupEnd();
   }

   console.groupEnd();
}

/**
 * Compares two objects and returns an array of differences
 * @param obj1 - The first object
 * @param obj2 - The second object
 * @param currentPath - Current path being traversed (used internally)
 * @param maxDepth - Maximum depth to traverse
 * @param currentDepth - Current depth (used internally)
 */
export function getObjectDifferences(
   obj1: any,
   obj2: any,
   currentPath: string = '',
   maxDepth: number = 10,
   currentDepth: number = 0,
): ObjectDifference[] {
   const differences: ObjectDifference[] = [];

   // Prevent infinite recursion
   if (currentDepth > maxDepth) {
      return differences;
   }

   // Handle null/undefined cases
   if (obj1 === null || obj1 === undefined) {
      if (obj2 !== null && obj2 !== undefined) {
         differences.push({
            path: currentPath || 'root',
            oldValue: obj1,
            newValue: obj2,
            type: 'added',
         });
      }
      return differences;
   }

   if (obj2 === null || obj2 === undefined) {
      differences.push({
         path: currentPath || 'root',
         oldValue: obj1,
         newValue: obj2,
         type: 'removed',
      });
      return differences;
   }

   // Handle primitive values
   if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
      if (obj1 !== obj2) {
         differences.push({
            path: currentPath || 'root',
            oldValue: obj1,
            newValue: obj2,
            type: 'modified',
         });
      }
      return differences;
   }

   // Handle arrays
   if (Array.isArray(obj1) && Array.isArray(obj2)) {
      const maxLength = Math.max(obj1.length, obj2.length);
      for (let i = 0; i < maxLength; i++) {
         const path = currentPath ? `${currentPath}[${i}]` : `[${i}]`;
         if (i >= obj1.length) {
            differences.push({
               path,
               oldValue: undefined,
               newValue: obj2[i],
               type: 'added',
            });
         } else if (i >= obj2.length) {
            differences.push({
               path,
               oldValue: obj1[i],
               newValue: undefined,
               type: 'removed',
            });
         } else {
            differences.push(...getObjectDifferences(obj1[i], obj2[i], path, maxDepth, currentDepth + 1));
         }
      }
      return differences;
   }

   // Handle objects
   const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

   for (const key of allKeys) {
      const path = currentPath ? `${currentPath}.${key}` : key;
      const hasKey1 = Object.prototype.hasOwnProperty.call(obj1, key);
      const hasKey2 = Object.prototype.hasOwnProperty.call(obj2, key);

      if (!hasKey1 && hasKey2) {
         differences.push({
            path,
            oldValue: undefined,
            newValue: obj2[key],
            type: 'added',
         });
      } else if (hasKey1 && !hasKey2) {
         differences.push({
            path,
            oldValue: obj1[key],
            newValue: undefined,
            type: 'removed',
         });
      } else if (hasKey1 && hasKey2) {
         differences.push(...getObjectDifferences(obj1[key], obj2[key], path, maxDepth, currentDepth + 1));
      }
   }

   return differences;
}

/**
 * Formats a value for display in console.table (more compact)
 */
function formatValueForTable(value: any): string {
   if (value === null) return 'null';
   if (value === undefined) return 'undefined';
   if (typeof value === 'string') return `"${value}"`;
   if (typeof value === 'boolean') return String(value);
   if (typeof value === 'number') return String(value);
   if (typeof value === 'object') {
      if (Array.isArray(value)) {
         if (value.length === 0) return '[]';
         if (value.length <= 3) {
            return `[${value.map((v) => formatValueForTable(v)).join(', ')}]`;
         }
         return `[${value.length} items]`;
      }

      const keys = Object.keys(value);
      if (keys.length === 0) return '{}';
      if (keys.length === 1) {
         return `{${keys[0]}: ${formatValueForTable(value[keys[0]])}}`;
      }
      return `{${keys.length} props}`;
   }
   return String(value);
}
